import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import logo from '../../assets/images/The-Key-Logo.png';
import apiClient from "../../utils/api";

function ParkingCombinedTicket() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [parking, setParking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [damageMarks, setDamageMarks] = useState([]);

  // Fetch parking data using the ID from URL params
  useEffect(() => {
    const fetchParkingData = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const token = localStorage.getItem('auth_token');
        const response = await apiClient.get(`accounts/parkings/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });        
        console.log('API response:', response.data.data);
        
        // Store the parking data
        const parkingData = response.data.data;
        setParking(parkingData);
        
        // If there's damage markers data, parse it
        if (parkingData.damage_marks) {
          try {
            const markers = JSON.parse(parkingData.damage_marks);
            console.log('Parsed damage markers:', markers);
            if (markers && Array.isArray(markers) && markers.length > 0) {
              console.log(`Found ${markers.length} damage marks with coordinates:`, 
                markers.map(m => `(${m.x},${m.y})`).join(', '));
              setDamageMarks(markers);
            } else {
              console.warn('Damage marks array is empty or invalid');
              setDamageMarks([]);
            }
          } catch (e) {
            console.error('Error parsing damage markers:', e);
            setDamageMarks([]);
          }
        } else {
          console.log('No damage_marks field found in response');
          setDamageMarks([]);
        }
        
        // Preload the car image to ensure it's in cache
        if (parkingData.has_damage) {
          const img = new Image();
          img.src = `${window.location.origin}/carimage2.png`;
          console.log('Preloading car image:', img.src);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching parking data:', err);
        setError('Failed to fetch parking data');
        setLoading(false);
      }
    };    
    fetchParkingData();
  }, [id]);

  useEffect(() => {
    if (!parking) return;
    
    console.log('Printing combined tickets:', parking);
    // Add a small delay to ensure the component is fully rendered
    setTimeout(() => {
      printTicket();
    }, 500);
  }, [parking]); // Re-run when parking data changes

  const printTicket = async () => {
    // Extract ticket data for printing
    const {
      ticket_number: serial = '00001',
      plate_number: plate = '',
      parking_time = '',
      parking_date = '',
      parking_type = '',
      qr_code_url = '',
      qr_code_base64 = '',
      exit_location_logo_url = '',
      exit_location_logo_base64 = '',
      exit_location = null,
    } = parking || {};

    const parkingTypeDisplay = parking_type === 'Complementary' ? 'FREE' : (parking_type || '-');
    const plateDisplay = plate || '-';
    const parkingDateTime = (parking_time && parking_date) ? `${parking_date} ${parking_time}` : '-';
    const serialDisplay = serial || '-';
    const locationDisplay = exit_location.name;

    // Prepare QR code data for printing
    let qrCodeBase64 = '';
    let qrCodeForPrint = '';

    // Prepare exit location logo data for printing
    let logoBase64 = '';
    let logoForPrint = '';

    // Process exit location logo
    if (exit_location_logo_base64 || (exit_location && exit_location.logo_base64)) {
      const base64Data = exit_location_logo_base64 || exit_location.logo_base64;
      logoBase64 = `data:image/png;base64,${base64Data}`;
      // For thermal printing, resize to 30mm width
      logoForPrint = await resizeImageForThermalPrint(logoBase64, 30);
    }
    else if (exit_location_logo_url || (exit_location && exit_location.logo_url)) {
      try {
        const logoUrl = exit_location_logo_url || exit_location.logo_url;
        const response = await fetch(logoUrl);
        const blob = await response.blob();
        const reader = new FileReader();
        const result = await new Promise((resolve) => {
          reader.onload = () => {
            resolve(reader.result);
          };
          reader.readAsDataURL(blob);
        });

        // Store the full base64 data URL for display
        logoBase64 = result;

        // For thermal printing, resize to 30mm width
        logoForPrint = await resizeImageForThermalPrint(result, 30);

      } catch (error) {
        console.error('Error fetching logo:', error);
      }
    }

    // Process QR code
    if (qr_code_base64) {
      // We already have the base64 data from the API
      qrCodeBase64 = `data:image/png;base64,${qr_code_base64}`;
      // For thermal printing, resize to 30mm width
      qrCodeForPrint = await resizeImageForThermalPrint(qrCodeBase64, 30);
    }
    else if (qr_code_url) {
      try {
        const response = await fetch(qr_code_url);
        const blob = await response.blob();
        const reader = new FileReader();
        const result = await new Promise((resolve) => {
          reader.onload = () => {
            resolve(reader.result);
          };
          reader.readAsDataURL(blob);
        });

        // Store the full base64 data URL for display
        qrCodeBase64 = result;

        // For thermal printing, resize to 30mm width
        qrCodeForPrint = await resizeImageForThermalPrint(result, 30);

      } catch (error) {
        console.error('Error fetching QR code:', error);
      }
    }
    
    // Ensure the damage marks are properly parsed
    if (parking.has_damage && (!damageMarks || damageMarks.length === 0)) {
      // This will ensure we're getting the damage marks even if state isn't updated
      try {
        const parsedMarks = JSON.parse(parking.damage_marks || '[]');
        if (parsedMarks.length > 0) {
          setDamageMarks(parsedMarks); // Update the state
        }
      } catch (e) {
        console.error('Failed to parse damage marks from raw data:', e);
      }
    }

    // Create ticket content for thermal printer - CUSTOMER TICKET
    const customerTicketContent = `
${'-'.repeat(32)}
     THE KEY VALET SERVICES
${'-'.repeat(32)}
Ticket No: ${serialDisplay}
${parkingTypeDisplay}
${'.'.repeat(32)}
     Valet Parking Ticket
${'-'.repeat(32)}
Plate No.     : ${plateDisplay}
Parking Time  : ${parkingDateTime}
${'-'.repeat(32)}
  Your car is in safe hands
${'.'.repeat(32)}
Please order your car 15mins in advance. Dial ${parking.exit_location?.phone_number || '+974 5538 0499'} for assistance.

${'-'.repeat(32)}
`;

    // Create ticket content for thermal printer - CAR TICKET
    const carTicketContent = `
${'-'.repeat(32)}
     THE KEY VALET SERVICES
${'-'.repeat(32)}
PARKING LOCATION:
${locationDisplay}
${'-'.repeat(32)}
Ticket No: ${serialDisplay}
${parkingTypeDisplay}
${'-'.repeat(32)}
CAR DETAILS:
Plate No.     : ${plateDisplay}
Parking Time  : ${parkingDateTime}
${'-'.repeat(32)}
Damage: ${parking.has_damage ? 'YES' : 'NO'}
${'-'.repeat(32)}
CONTACT US:
INFO@THEKEYVALET.COM
+974 3377 7441
${'-'.repeat(32)}
`;

    // Create ticket content for thermal printer - KEY TICKET
    const keyTicketContent = `
${'-'.repeat(32)}
     THE KEY VALET SERVICES
${'-'.repeat(32)}
Ticket No: ${serialDisplay}
${parkingTypeDisplay}
${'-'.repeat(32)}
Plate No.     : ${plateDisplay}
Parking Time  : ${parkingDateTime}
${'-'.repeat(32)}
`;

    await androidPrintTickets(customerTicketContent, carTicketContent, keyTicketContent, qrCodeForPrint, logoForPrint);
  };

  // Helper function to resize images for thermal printing
  const resizeImageForThermalPrint = (dataUrl, targetWidthMm) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Convert mm to pixels (assuming 203 DPI for thermal printer)
        const dpi = 203;
        const mmToPx = dpi / 25.4;
        const targetWidthPx = Math.round(targetWidthMm * mmToPx);
        
        // Calculate height to maintain aspect ratio
        const aspectRatio = img.height / img.width;
        const targetHeightPx = Math.round(targetWidthPx * aspectRatio);
        
        // Set canvas dimensions
        canvas.width = targetWidthPx;
        canvas.height = targetHeightPx;
        
        // Draw resized image
        ctx.drawImage(img, 0, 0, targetWidthPx, targetHeightPx);
        
        // Convert back to data URL
        const resizedDataUrl = canvas.toDataURL('image/png');
        resolve(resizedDataUrl);
      };
      
      img.src = dataUrl;
    });
  };

  // Helper function to create a combined image with logo and QR code side by side
  const createCombinedLogoAndQR = (logoData, qrData) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas size for thermal printer (72mm width)
      const dpi = 203;
      const mmToPx = dpi / 25.4;
      const canvasWidth = Math.round(72 * mmToPx); // 72mm width
      const imageWidth = Math.round(30 * mmToPx);  // 30mm for each image
      const spacing = Math.round(12 * mmToPx);     // 12mm spacing between images
      
      let imagesLoaded = 0;
      const logoImg = new Image();
      const qrImg = new Image();
      
      const checkIfBothLoaded = () => {
        imagesLoaded++;
        if (imagesLoaded === 2) {
          // Calculate heights maintaining aspect ratio
          const logoAspectRatio = logoImg.height / logoImg.width;
          const qrAspectRatio = qrImg.height / qrImg.width;
          
          const logoHeight = Math.round(imageWidth * logoAspectRatio);
          const qrHeight = Math.round(imageWidth * qrAspectRatio);
          
          // Canvas height should accommodate the taller image
          const canvasHeight = Math.max(logoHeight, qrHeight);
          canvas.width = canvasWidth;
          canvas.height = canvasHeight;
          
          // Fill with white background
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvasWidth, canvasHeight);
          
          // Draw logo on the left
          const logoX = Math.round((canvasWidth / 2 - spacing / 2) / 2 - imageWidth / 2);
          const logoY = Math.round((canvasHeight - logoHeight) / 2);
          ctx.drawImage(logoImg, logoX, logoY, imageWidth, logoHeight);
          
          // Draw QR code on the right
          const qrX = Math.round(canvasWidth / 2 + spacing / 2 + (canvasWidth / 2 - spacing / 2) / 2 - imageWidth / 2);
          const qrY = Math.round((canvasHeight - qrHeight) / 2);
          ctx.drawImage(qrImg, qrX, qrY, imageWidth, qrHeight);
          
          // Convert to data URL
          const combinedDataUrl = canvas.toDataURL('image/png');
          resolve(combinedDataUrl);
        }
      };
      
      logoImg.onload = checkIfBothLoaded;
      qrImg.onload = checkIfBothLoaded;
      
      logoImg.src = logoData;
      qrImg.src = qrData;
    });
  };

  const androidPrintTickets = async (customerTicket, carTicket, keyTicket, qrCodeData = '', logoData = '') => {
    try {
      const beforeUrl = 'intent:';
      const afterUrl = '#Intent;scheme=rawbt;package=ru.a402d.rawbtprinter;end;';
      
      // Create the print queue with appropriate delays
      const printQueue = [];

      // Step 1: Add logo and QR code first
      if (logoData && qrCodeData) {
        try {
          const combinedImage = await createCombinedLogoAndQR(logoData, qrCodeData);
          printQueue.push(() => {
            console.log('Step 1: Printing combined logo and QR code');
            try {
              const combinedIntentUrl = `intent:${combinedImage}#Intent;scheme=rawbt;package=ru.a402d.rawbtprinter;end;`;
              window.location.href = combinedIntentUrl;
            } catch (combinedError) {
              console.error('Error printing combined image:', combinedError);
              try {
                const directCombinedUrl = `rawbt:${combinedImage}`;
                window.location.href = directCombinedUrl;
                console.log('Fallback: Using direct rawbt:data scheme for combined image');
              } catch (directError) {
                console.error('Combined image printing failed:', directError);
              }
            }
            return 1500; // Return delay before next item (combined image needs more time)
          });
        } catch (error) {
          console.error('Error creating combined image, falling back to separate images:', error);
          // Fall back to separate images
          if (logoData) {
            printQueue.push(() => {
              console.log('Step 1a: Printing exit location logo');
              try {
                const logoIntentUrl = `intent:${logoData}#Intent;scheme=rawbt;package=ru.a402d.rawbtprinter;end;`;
                window.location.href = logoIntentUrl;
              } catch (logoError) {
                console.error('Error printing exit location logo:', logoError);
                try {
                  const directLogoUrl = `rawbt:${logoData}`;
                  window.location.href = directLogoUrl;
                  console.log('Fallback: Using direct rawbt:data scheme for logo');
                } catch (directError) {
                  console.error('Logo printing failed:', directError);
                }
              }
              return 1000;
            });
          }

          if (qrCodeData) {
            printQueue.push(() => {
              console.log('Step 1b: Printing QR code');
              try {
                const qrIntentUrl = `intent:${qrCodeData}#Intent;scheme=rawbt;package=ru.a402d.rawbtprinter;end;`;
                window.location.href = qrIntentUrl;
              } catch (qrError) {
                console.error('Error printing QR code:', qrError);
                try {
                  const directQrUrl = `rawbt:${qrCodeData}`;
                  window.location.href = directQrUrl;
                  console.log('Fallback: Using direct rawbt:data scheme for QR code');
                } catch (directError) {
                  console.error('QR code printing failed:', directError);
                }
              }
              return 1500; 
            });
          }
        }
      } else {
        // Handle individual images if only one is available
        if (logoData) {
          printQueue.push(() => {
            console.log('Step 1a: Printing exit location logo');
            try {
              const logoIntentUrl = `intent:${logoData}#Intent;scheme=rawbt;package=ru.a402d.rawbtprinter;end;`;
              window.location.href = logoIntentUrl;
            } catch (logoError) {
              console.error('Error printing exit location logo:', logoError);
              try {
                const directLogoUrl = `rawbt:${logoData}`;
                window.location.href = directLogoUrl;
                console.log('Fallback: Using direct rawbt:data scheme for logo');
              } catch (directError) {
                console.error('Logo printing failed:', directError);
              }
            }
            return 1000;
          });
        }

        if (qrCodeData) {
          printQueue.push(() => {
            console.log('Step 1b: Printing QR code');
            try {
              const qrIntentUrl = `intent:${qrCodeData}#Intent;scheme=rawbt;package=ru.a402d.rawbtprinter;end;`;
              window.location.href = qrIntentUrl;
            } catch (qrError) {
              console.error('Error printing QR code:', qrError);
              try {
                const directQrUrl = `rawbt:${qrCodeData}`;
                window.location.href = directQrUrl;
                console.log('Fallback: Using direct rawbt:data scheme for QR code');
              } catch (directError) {
                console.error('QR code printing failed:', directError);
              }
            }
            return 1500; 
          });
        }
      }
      
      // Step 2: Add customer ticket to queue
      printQueue.push(() => {
        console.log('Step 2: Printing customer ticket...');
        const customerEncoded = encodeURIComponent(customerTicket);
        const customerIntentUrl = beforeUrl + customerEncoded + afterUrl;
        window.location.href = customerIntentUrl;
        return 500; // Delay before next item
      });
      
      // Step 3: Add car ticket to queue
      printQueue.push(() => {
        console.log('Step 3: Printing car ticket...');
        const carEncoded = encodeURIComponent(carTicket);
        const carIntentUrl = beforeUrl + carEncoded + afterUrl;
        window.location.href = carIntentUrl;
        return 1500; // Return delay before next item
      });
      
      // Step 4: Add key ticket to queue
      printQueue.push(() => {
        console.log('Step 4: Printing key ticket...');
        const keyEncoded = encodeURIComponent(keyTicket);
        const keyIntentUrl = beforeUrl + keyEncoded + afterUrl;
        window.location.href = keyIntentUrl;
        console.log('All tickets printed.');
        return 1500; // No delay needed after last item
      });
      
      // Execute the print queue sequentially with delays
      const executePrintQueue = (index) => {
        if (index >= printQueue.length) {
          console.log('Print queue completed');
          return; // Done with the queue
        }
        
        console.log(`Executing print queue item ${index + 1} of ${printQueue.length}`);
        const printFunction = printQueue[index];
        const delay = printFunction();
        setTimeout(() => {
          executePrintQueue(index + 1);
        }, delay);
      };
      
      // Debug: Show the queue length before execution
      console.log(`Print queue initialized with ${printQueue.length} items`);
      
      // Start executing the print queue with a small initial delay
      executePrintQueue(0);
    } catch (error) {
      console.error('Error sending to Android RawBT app:', error);
    }
  };

  // If loading, show loading indicator
  if (loading) {
    return <div>Loading ticket data...</div>;
  }

  // If error, show error message
  if (error) {
    return <div>Error: {error}</div>;
  }

  // If no parking data, show message
  if (!parking) {
    return <div>No parking data available</div>;
  }

  // Extract ticket data from the parking object
  const {
    ticket_number: serial = '00001',
    plate_number: plate = '',
    model = null,
    color = null,
    brand = null,
    parking_time = '',
    parking_date = '',
    parking_type = '',
    has_damage = false,
    exit_location = null,
  } = parking || {};
  
  // Extract values from relationship objects
  const modelName = model?.name || '-';
  const colorName = color?.name || '-';
  const brandName = brand?.name || '-';   
  // Determine what to display for the parking type label
  const parkingTypeDisplay = parking_type === 'Complementary' ? 'FREE' : (parking_type || '-');
  const plateDisplay = plate || '-';
  const parkingDateTime = parking_time && parking_date ? 
    `${parking_date} ${parking_time}` : '-';
  const serialDisplay = serial || '-';
  const locationDisplay = exit_location?.name || '-';

  return (
    <div className="print-combined-tickets">
      <style>
        {`
        @page {
          margin: 0;
          size: 80mm auto;
        }
        body {
          margin: 0;
          padding: 0;
          background: white;
          font-family: Arial, sans-serif;
        }
        .print-combined-tickets {
          width: 72mm;
          margin: 0 auto;
        }
        .ticket-gap {
          height: 150px;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .cutting-line {
          width: 100%;
          border-top: 1px dashed #000;
          position: absolute;
          top: 50%;
          left: 0;
        }
        .cut-text {
          background: white;
          padding: 0 10px;
          font-size: 10px;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
        
        /* Customer Ticket Styles */
        .customer-ticket {
          width: 72mm;
          padding: 1mm;
          font-family: Arial, sans-serif;
          color: #333;
          text-align: center;
        }
        .ticket-header {
          text-align: center;
          margin-bottom: 1mm;
        }
        .ticket-logo {
          width: 25mm;
          margin-bottom: 1mm;
        }        
        .ticket-serial {
          margin: 1mm 0;
          display: flex;
          align-items: center;
          justify-content: left;
          gap: 2mm;
        }
        .ticket-serial-label {
          font-size: 3mm;
        }
        .serial-number {
          color: #FF0000;
          font-size: 6mm;
          font-weight: bold;
        }
        .free-label {
          color: #967c3d;
          font-size: 4mm;
          margin: 1mm 0;
          text-align: left;
        }
        .dotted-divider {
          border: none;
          border-top: 0.5mm dotted #FF0000;
          margin: 1mm 0;
        }
        .ticket-title {
          background: #967c3d;
          color: white;
          padding: 1mm;
          text-align: center;
          border-radius: 1mm;
          margin-bottom: 1mm;
        }
        .ticket-details {
          width: 100%;
          margin-bottom: 1mm;
          border-collapse: collapse;
        }
        .ticket-details td {
          padding: 1mm;
          font-size: 2.5mm;
          text-align: left;
        }
        .arabic-text {
          color: #967c3d;
          font-size: 2.5mm;
        }
        .logo-large {
          text-align: center;
          margin: 1mm 0;
        }
        .logo-large img {
          width: 35mm;
        }
        .safe-hands {
          text-align: center;
          border: 0.2mm solid #967c3d;
          border-radius: 3mm;
          padding: 1mm;
          margin: 1mm 0;
        }
        .footer {
          text-align: center;
        }
        .order-text {
          border: 0.2mm solid #967c3d;
          border-radius: 3mm;
          padding: 1mm;
          margin: 1mm 0;
        }
        .phone-number {
          font-size: 3mm;
          margin: 1mm 0;
        }
        .exit-location-logo-section, 
        .qr-code-section {
          margin: 2mm 0;
        }
        .exit-location-logo-container,
        .qr-code-container {
          text-align: center;
        }
        .exit-location-logo-image {
          max-width: 40mm;
          max-height: 20mm;
          margin: 1mm auto;
          display: block;
        }
        .qr-code-image {
          width: 20mm;
          height: 20mm;
          margin: 1mm auto;
          display: block;
        }
        
        /* Car Ticket Styles */
        .car-ticket {
          width: 72mm;
          padding: 2mm;
          font-family: Arial, sans-serif;
          color: #333;
        }
        .car-ticket .ticket-header {
          text-align: center;
          background: #967c3d;
          color: white;
          padding: 2mm;
          border-radius: 1mm;
          margin-bottom: 2mm;
        }
        .ticket-section {
          margin: 2mm 0;
          padding: 2mm;
        }
        .section-title {
          text-align: center;
          font-weight: bold;
          margin: 2mm 0;
          font-size: 14px;
        }
        .detail-row {
          display: flex;
          justify-content: space-between;
          margin: 1mm 0;
          font-size: 12px;
        }
        .detail-label {
          font-weight: bold;
          min-width: 80px;
        }
        .car-diagram {
          margin: 2mm 0;
          text-align: center;
        }
        .car-diagram h3 {
          font-size: 12px;
          margin: 2mm 0;
        }
        .car-damage-view {
          margin: 2mm auto;
          page-break-inside: avoid;
        }
        .damage-checkbox {
          margin: 2mm 0;
          font-size: 10px;
        }
        .checkbox-item {
          display: flex;
          align-items: center;
          margin: 1mm 0;
        }
        .checkbox-item input {
          margin-right: 2mm;
        }
        .terms {
          margin: 2mm 0;
          font-size: 8px;
          text-align: center;
        }
        .contact {
          text-align: center;
          margin-top: 3mm;
        }
        .contact h2 {
          color: #967c3d;
          margin-bottom: 1mm;
          font-size: 12px;
        }
        .contact-info {
          color: #967c3d;
          font-size: 10px;
        }
        
        /* Key Ticket Styles */
        .key-ticket {
          width: 72mm;
          padding: 1mm;
          font-family: Arial, sans-serif;
          color: #333;
          text-align: center;
        }
        .ticket-divider {
          margin: 2mm 0;
          border: none;
          border-top: 1px dashed #000;
        }
        
        @media print {
          .print-combined-tickets { 
            width: 72mm;
            margin: 0;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .ticket-title, .car-ticket .ticket-header {
            background: #967c3d !important;
            color: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .serial-number {
            color: #FF0000 !important;
          }
          .free-label, .arabic-text {
            color: #967c3d !important;
          }
          .exit-location-logo-image {
            max-width: 40mm !important;
            max-height: 20mm !important;
          }
          .qr-code-image {
            width: 20mm !important;
            height: 20mm !important;
          }
          .contact h2, .contact-info {
            color: #967c3d !important;
          }
          .cutting-line {
            border-top: 1px dashed #000 !important;
          }
        }
        `}
      </style>      
      
      {/* CUSTOMER TICKET */}
      <div className="customer-ticket">
        <div className="ticket-header">
          <img src={logo} alt="Key Valet" className="ticket-logo" />
          <div className="ticket-serial">
            <span className="ticket-serial-label">Ticket No:</span>
            <span className="serial-number">{serialDisplay}</span>
          </div>
          <div className="free-label" style={{ paddingLeft: '1mm' }}>{parkingTypeDisplay}</div>
        </div>

        <hr className="dotted-divider" />

        <div className="ticket-title">
          <div className="arabic-text">بطاقة صف السيارة</div>
          <div>Valet Parking Ticket</div>
        </div>

        <table className="ticket-details">
          <tbody>
            <tr>
              <td>Plate No.:<br/><span className="arabic-text">رقم اللوحة:</span></td>
              <td>{plateDisplay}</td>
              <td>Car Model:<br/><span className="arabic-text">نوع السيارة:</span></td>
              <td>{modelName}</td>
            </tr>
            <tr>
              <td>Color:<br/><span className="arabic-text">اللون:</span></td>
              <td>{colorName}</td>
              <td>Brand:<br/><span className="arabic-text">الماركة:</span></td>
              <td>{brandName}</td>
            </tr>
            <tr>
              <td>Parking Time:<br/><span className="arabic-text">وقت الوقوف:</span></td>
              <td>{parkingDateTime}</td>
            </tr>
          </tbody>
        </table>

        <hr className="dotted-divider" />

        <div className="logo-large">
          <img src={logo} alt="Key Valet Large" />
        </div>

        <div className="safe-hands">
          <div className="arabic-text">سيارتك في أيدٍ أمينة</div>
          <div>Your car is in safe hands</div>
        </div>

        <hr className="dotted-divider" />

        <div className="footer">
          <div className="order-text">
            <div className="arabic-text">يرجى طلب سيارتك قبل 15 دقيقة</div>
            <div>Please order your car 15mins in advance. </div>
          </div>
          <div className="phone-number">
            <i className="fa fa-phone"></i> Dial {parking.exit_location?.phone_number || '+974 5538 0499'} for assistance.
          </div>
          
          {/* QR Code Display */}
          {(parking.qr_code_base64 || parking.qr_code_url) && (
            <div className="qr-code-section">
              <hr className="dotted-divider" />
              <div className="qr-code-container">
                <img 
                  src={parking.qr_code_base64 
                    ? `data:image/png;base64,${parking.qr_code_base64}` 
                    : parking.qr_code_url
                  } 
                  alt="QR Code" 
                  className="qr-code-image" 
                />
                <div style={{ fontSize: '2mm', marginTop: '1mm' }}>Scan for tracking</div>
              </div>
            </div>
          )}
          
          <div className="free-label">{parkingTypeDisplay}</div>
          <div className="serial-number">{serialDisplay}</div>
        </div>
      </div>
      
      {/* GAP WITH CUTTING LINE */}
      <div className="ticket-gap">
        <div className="cutting-line"></div>
        <div className="cut-text">✄ CUT HERE ✄</div>
      </div>

      {/* CAR TICKET */}
      <div className="car-ticket">
        <div className="ticket-header">
          THE KEY VALET SERVICES
        </div>

        <hr className="dotted-divider" />
        
        <div className="ticket-section">
          <div className="section-title">PARKING LOCATION</div>
          <div className="detail-row">
            <span>{locationDisplay}</span>
          </div>
        </div>

        <hr className="dotted-divider" />
        
        <div className="ticket-section">
          <div className="detail-row">
            <span className="detail-label">Ticket No:</span>
            <span>{serialDisplay}</span>
          </div>
          <div className="detail-row">
            <span>{parkingTypeDisplay}</span>
          </div>
        </div>

        <hr className="dotted-divider" />

        <div className="ticket-section">
          <div className="section-title">CAR DETAILS</div>
          <div className="ticket-details">
            <div className="detail-row">
              <span className="detail-label">Plate No.:</span>
              <span>{plateDisplay}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Car Model:</span>
              <span>{modelName}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Color:</span>
              <span>{colorName}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Parking Time:</span>
              <span>{parkingDateTime}</span>
            </div>
          </div>
        </div>

        {has_damage && (
          <div className="car-diagram">
            {/* <h3>CAR INSPECTION DIAGRAM</h3>
            <div 
              className="car-damage-view" 
              style={{ 
                position: 'relative', 
                width: '300px',  
                height: '300px', 
                margin: '0 auto' 
              }}
            >
              <img
                src={`${window.location.origin}/carimage2.png`}
                alt="Car Diagram"
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '10px',
                  border: '1px solid #ddd'
                }}
              />          
              {damageMarks.map((dot, index) => {
                // The dots coming from ParkingEdit are in actual screen coordinates (around 400px width)
                // We need to convert them to our 300x300 display size
                const originalImgWidth = 400; // From ParkingEdit's maxWidth for the car image
                const targetImgWidth = 300;   // Our fixed width in ParkingCombinedTicket
                
                // Scale factor to convert from original coordinates to our display size
                const scaleFactor = targetImgWidth / originalImgWidth;
                
                // Calculate scaled positions
                const scaledX = Math.round(dot.x * scaleFactor);
                const scaledY = Math.round(dot.y * scaleFactor);
                
                // Dot size
                const dotSize = 8;
                const dotRadius = dotSize / 2;
                
                console.log(`Damage mark ${index}: Original (${dot.x}, ${dot.y}) -> Scaled for print (${scaledX}, ${scaledY})`);
                
                return (
                  <div
                    key={index}
                    style={{
                      position: 'absolute',
                      top: (scaledY - dotRadius) + 'px',
                      left: (scaledX - dotRadius) + 'px',
                      width: dotSize + 'px',
                      height: dotSize + 'px',
                      borderRadius: '50%',
                      backgroundColor: 'red',
                      border: '1px solid white'
                    }}
                  />
                );
              })}
            </div> */}
            
            {/* Show damage image if available */}
            {parking.damage_image_url && (
              <div style={{ marginTop: '15px', textAlign: 'center' }}>
                <h4 style={{ fontSize: '14px', marginBottom: '8px', fontWeight: 'bold' }}>DAMAGE IMAGE</h4>
                <img 
                  src={parking.damage_image_url} 
                  alt="Vehicle Damage" 
                  style={{
                    maxWidth: '280px',
                    maxHeight: '200px',
                    border: '1px solid #ddd',
                    borderRadius: '5px'
                  }}
                />
              </div>
            )}
          </div>
        )}

        <div className="damage-checkbox">
          <div className="checkbox-item">
            <input 
              type="checkbox" 
              id="no-damage" 
              checked={!has_damage}
              readOnly 
            />
            <label htmlFor="no-damage">There is no damage.</label>
          </div>
          <div className="checkbox-item">
            <input 
              type="checkbox" 
              id="has-damage" 
              checked={has_damage}
              readOnly 
            />
            <label htmlFor="has-damage">There are damages.</label>
          </div>
        </div>

        <hr className="dotted-divider" />

        <div className="logo-section">
          <img src={logo} alt="The Key Valet" style={{ width: '40mm' }} />
        </div>

        <div className="terms">
          <p>
            Any individual operating the car represents
            the driver and the owner of the car.
            Please report any damage to the car before handling it.
            The company is not responsible for any damage
            caused by natural catastrophes, theft, accidents,
            or mechanical failure.
            No employee has the authority to modify these terms.
          </p>
        </div>

        <hr className="dotted-divider" />

        <div className="contact">
          <h2>CONTACT US</h2>
          <div className="contact-info">
            <div>INFO@THEKEYVALET.COM</div>
            <div>+974 3377 7441</div>
          </div>
        </div>
      </div>
      
      {/* GAP WITH CUTTING LINE */}
      <div className="ticket-gap">
        <div className="cutting-line"></div>
        <div className="cut-text">✄ CUT HERE ✄</div>
      </div>

      {/* KEY TICKET */}
      <div className="key-ticket">
        <div className="ticket-header">
          <img src={logo} alt="Key Valet" className="ticket-logo" />
        </div>

        <hr className="ticket-divider" />
        <div className="ticket-title">THE KEY VALET SERVICES</div>
        <hr className="ticket-divider" />

        <div className="ticket-serial">
          Ticket No: {serialDisplay}
        </div>
        <div className="parking-type">{parkingTypeDisplay}</div>

        <hr className="ticket-divider" />

        <table className="ticket-details">
          <tbody>
            <tr>
              <td>Plate No.</td>
              <td>: {plateDisplay}</td>
            </tr>
            <tr>
              <td>Car Model</td>
              <td>: {modelName}</td>
            </tr>
            <tr>
              <td>Color</td>
              <td>: {colorName}</td>
            </tr>
            <tr>
              <td>Parking Time</td>
              <td>: {parkingDateTime}</td>
            </tr>
          </tbody>
        </table>

        <hr className="ticket-divider" />
      </div>
    </div>
  );
}

export default ParkingCombinedTicket;